/**
 * Randomizer — generates fresh parameter values for a list of modules.
 *
 * The strategy:
 *
 *   Numeric params    — sample a fresh normalized target in [0, 1], then
 *                        lerp from the current normalized value by `amount`.
 *                        amount=0 is a no-op; amount=1 is a full jump.
 *                        Per-param `randomSkew` biases the sampled target
 *                        via `target = Math.pow(random, 1/skew)`: skew > 1
 *                        biases toward high values, skew < 1 toward low.
 *   Enum params       — with probability `amount`, pick a random option.
 *                        Otherwise leave it alone.
 *   randomizable=false — always skipped.
 *
 * Callers typically pass the same `SynthModule[]` they pass to PresetManager.
 * The `source: 'randomize'` tag on parameter writes lets downstream code
 * (e.g. a future "undo last randomize" feature) distinguish dice-driven
 * changes from user drags.
 */

import type { SynthModule } from '../audio/modules/SynthModule';
import type { AnyParameter } from './Parameter';
import type { ModRouter } from '../audio/modulation/ModRouter';
import type { ModTarget } from '../audio/modulation/targets';
import { clampDepth } from '../audio/modulation/ModConnection';

export interface RandomizerOptions {
  /** 0..1, how far to move each param toward a random target. Default 1 (full). */
  readonly amount?: number;
  /** Seeded RNG for tests. Returns a value in [0, 1). */
  readonly rng?: () => number;
}

export class Randomizer {
  randomizeModules(
    modules: readonly SynthModule[],
    opts: RandomizerOptions = {},
  ): void {
    const amount = Math.max(0, Math.min(1, opts.amount ?? 1));
    if (amount === 0) return;
    const rng = opts.rng ?? Math.random;
    for (const module of modules) {
      for (const param of module.parameters) {
        this.randomizeParam(param, amount, rng);
      }
    }
  }

  /**
   * Randomize a flat list of parameters. Used for things that aren't inside a
   * SynthModule — e.g. voice-level Glide in VoiceControls. Same skew/amount
   * semantics as `randomizeModules`.
   */
  randomizeParams(params: readonly AnyParameter[], opts: RandomizerOptions = {}): void {
    const amount = Math.max(0, Math.min(1, opts.amount ?? 1));
    if (amount === 0) return;
    const rng = opts.rng ?? Math.random;
    for (const param of params) {
      this.randomizeParam(param, amount, rng);
    }
  }

  /**
   * Shuffle an effect-chain order in place. Keeps every effect (no drops)
   * but picks a random permutation — only happens on full randomize; a
   * nudge leaves the chain order alone because reordering can change
   * the character of the patch dramatically.
   */
  randomizeEffectsOrder(
    chain: { getOrder(): readonly string[]; setOrder(order: readonly string[]): void },
    opts: RandomizerOptions = {},
  ): void {
    const amount = Math.max(0, Math.min(1, opts.amount ?? 1));
    if (amount < 1) return;
    const rng = opts.rng ?? Math.random;
    const next = [...chain.getOrder()];
    for (let i = next.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [next[i], next[j]] = [next[j]!, next[i]!];
    }
    chain.setOrder(next);
  }

  /**
   * Rebuild the effects bank from scratch — picks a random count
   * (clamped to `maxCount`/`minCount`) and a random type per slot
   * pulled from `availableTypes`. Duplicates are allowed, so a roll
   * can land on three distortions or two delays plus a phaser.
   *
   * `requiredTypes` are always included in the rebuilt bank — useful
   * for guaranteeing that signature effects (distortion, bitcrusher,
   * …) are part of every random patch. The minimum count is bumped
   * to at least `requiredTypes.length` so they always fit. The
   * remaining slots get random picks; the final list is shuffled so
   * required effects don't always land at the top of the chain.
   *
   * Only runs on full randomize (`amount === 1`); a nudge keeps the
   * current bank and only wiggles existing params, since adding /
   * removing entire effects changes the patch's character far more
   * than the user expects from a "shift-click" tweak.
   *
   * The chain interface intentionally only requires the methods the
   * randomizer touches (`addEffect` / `removeEffect` / `getOrder`),
   * which makes this easy to unit-test against a stub. Param
   * randomization for the freshly-minted effects is deferred to the
   * caller — they'll iterate the chain post-rebuild and call
   * `randomizeModules` on it.
   */
  randomizeEffectsBank(
    chain: {
      addEffect(type: string): unknown;
      removeEffect(id: string): boolean;
      getOrder(): readonly string[];
    },
    availableTypes: readonly string[],
    opts: RandomizerOptions & {
      readonly maxCount?: number;
      readonly minCount?: number;
      /**
       * Effect types that must appear in every rebuilt bank — added
       * first, then shuffled together with random picks so position
       * still varies. Duplicates allowed (`['dist', 'dist']` forces
       * two distortions). Bumps the floor of the count range to
       * `requiredTypes.length` if it would otherwise fall below.
       */
      readonly requiredTypes?: readonly string[];
      /**
       * Mutually-exclusive type groups — at most ONE member of each
       * group may appear in the rebuilt bank. Used for effects that
       * sound similar in character (chorus / flanger / phaser are
       * all modulation effects; stacking two of them muddies the
       * sound) so the random picker doesn't routinely combine them.
       *
       * Each subarray is one group. Types not in any group can still
       * duplicate freely. `requiredTypes` is honored as-is — the
       * caller is responsible for not contradicting their own
       * exclusivity constraints there.
       */
      readonly exclusiveGroups?: ReadonlyArray<readonly string[]>;
    } = {},
  ): void {
    const amount = Math.max(0, Math.min(1, opts.amount ?? 1));
    if (amount < 1) return;
    if (availableTypes.length === 0 && (opts.requiredTypes?.length ?? 0) === 0) {
      return;
    }
    const rng = opts.rng ?? Math.random;
    const required = opts.requiredTypes ?? [];
    const max = Math.max(required.length, opts.maxCount ?? 5);
    const min = Math.max(required.length, Math.min(opts.minCount ?? 1, max));

    // Tear down the current bank. Snapshot ids first since removeEffect
    // mutates the chain's order array under us.
    for (const id of [...chain.getOrder()]) {
      chain.removeEffect(id);
    }

    // Pick a count in [min, max] inclusive. The required types take
    // up the first `required.length` slots; remaining slots get fresh
    // random picks. Required types are excluded from the random pool
    // — they're guaranteed to appear once via the seed list and we
    // don't want a second instance to land on top by accident. Other
    // (non-required) types can still duplicate freely, which is what
    // makes the unconstrained random bank fun.
    const requiredSet = new Set(required);
    const extraPool = availableTypes.filter((t) => !requiredSet.has(t));
    const count = min + Math.floor(rng() * (max - min + 1));
    const types: string[] = [...required];

    // Exclusive-group bookkeeping. Once a member of a group is in the
    // bank, the rest of the group becomes ineligible. Required types
    // seed this set so picks made later in the loop respect any
    // group memberships the caller already locked in.
    const exclusiveGroups = opts.exclusiveGroups ?? [];
    const groupOf = (type: string): readonly string[] | null =>
      exclusiveGroups.find((g) => g.includes(type)) ?? null;
    const blockedTypes = new Set<string>();
    const lockGroup = (type: string): void => {
      const g = groupOf(type);
      if (!g) return;
      // Block every member of this group, including the one we just
      // added — duplicates of an exclusive type also count.
      for (const t of g) blockedTypes.add(t);
    };
    for (const t of required) lockGroup(t);

    // Bounded retry loop — pick from extraPool, skip if the type is
    // currently blocked by an exclusive group constraint, otherwise
    // accept and lock its group. We may exhaust the pool before
    // hitting `count` if exclusivity rules out enough types; that's
    // fine, the bank just ends up shorter (still ≥ required.length).
    const maxAttempts = count * 4 + 16;
    let attempts = 0;
    while (types.length < count && attempts < maxAttempts) {
      attempts++;
      const t = extraPool[Math.floor(rng() * extraPool.length)];
      if (t === undefined) break;
      if (blockedTypes.has(t)) continue;
      types.push(t);
      lockGroup(t);
    }
    // Fisher-Yates shuffle so required effects don't always sit at
    // the head of the chain — position contributes to the patch's
    // character in non-trivial ways.
    for (let i = types.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [types[i], types[j]] = [types[j]!, types[i]!];
    }
    for (const t of types) chain.addEffect(t);
  }

  /**
   * Rebuild the mod matrix with a fresh batch of connections. `amount = 1`
   * clears every existing connection and drops 2–4 new ones in; smaller
   * amounts just wiggle the existing depths in place so a nudge doesn't
   * destroy the routing the user was iterating on.
   *
   * Source selection is weighted so LFO/S&H show up most often (they're the
   * loudest "I hear modulation" sources), macros next, per-note last —
   * per-note only targets voice-scope params, since pairing them with a
   * single global AudioParam is non-musical (the router also enforces this).
   */
  randomizeModConnections(
    router: ModRouter,
    targets: readonly ModTarget[],
    opts: RandomizerOptions = {},
  ): void {
    const amount = Math.max(0, Math.min(1, opts.amount ?? 1));
    if (amount === 0) return;
    const rng = opts.rng ?? Math.random;

    // Nudge: just wiggle each existing depth by up to ±0.2 × amount so
    // "Shift-Randomize" keeps the current routing but shifts the feel.
    if (amount < 1) {
      for (const c of router.listConnections()) {
        const next = clampDepth(c.depth + (rng() * 2 - 1) * 0.2 * amount);
        router.setDepth(c.id, next);
      }
      return;
    }

    // Full randomize: tear down and rebuild. The two default connections
    // wired at startup (velocity → Cutoff, LFO → Cutoff) were the reason
    // every random patch seemed to wobble the same knob.
    for (const c of router.listConnections()) {
      router.removeConnection(c.id);
    }

    const sources = router.listSources();
    if (sources.length === 0 || targets.length === 0) return;

    // Bucket sources by kind so we can weight the draw — per-note only
    // makes sense against voice-scope targets, so we filter later.
    // The WAVE source (id: 'wave') is filtered out of random picks
    // because it taps the master output and feeding it back into a
    // signal-path AudioParam (filter cutoff, OSC level, anything in
    // the post-voice chain) creates an audio-rate feedback loop
    // that can run away to clipping in milliseconds. Users who
    // explicitly route WAVE to something in the matrix accept that
    // risk; the randomizer shouldn't impose it by surprise.
    const SAFE_SOURCE = (id: string): boolean => id !== 'wave';
    const modulators = sources.filter(
      (s) => s.category === 'Modulators' && SAFE_SOURCE(s.id),
    );
    const perNote = sources.filter((s) => s.category === 'Per-note');
    const macros = sources.filter((s) => s.category === 'Macros');

    const voiceTargets = targets.filter((t) => t.scope === 'voice');

    const count = 2 + Math.floor(rng() * 3); // 2..4 connections
    const made = new Set<string>(); // "sourceId::moduleId::param" — dedupe
    for (let i = 0; i < count; i++) {
      // Weighted source draw: 50% modulators, 25% macros, 25% per-note.
      const roll = rng();
      const bucket =
        roll < 0.5 && modulators.length > 0
          ? modulators
          : roll < 0.75 && macros.length > 0
            ? macros
            : perNote.length > 0
              ? perNote
              : modulators.length > 0
                ? modulators
                : sources;
      const source = bucket[Math.floor(rng() * bucket.length)];
      if (!source) continue;

      // Per-note sources only productively target voice-scope. Everything
      // else can target anything, with one caveat: a macro can't pick
      // itself (router rejects, but filtering upfront keeps the draw fair).
      let candidates =
        source.category === 'Per-note'
          ? voiceTargets
          : source.id.startsWith('macro-')
            ? targets.filter((t) => t.moduleId !== source.id)
            : targets;
      if (candidates.length === 0) continue;

      const target = candidates[Math.floor(rng() * candidates.length)];
      if (!target) continue;

      const key = `${source.id}::${target.moduleId}::${target.param}`;
      if (made.has(key)) {
        i--; // retry — this pair already picked
        // but bail after a few retries to avoid infinite loops on tiny target sets
        if (made.size >= Math.min(count * 2, candidates.length)) break;
        continue;
      }
      made.add(key);

      // Random depth in ±0.4 — capped lower than the old ±0.6 because
      // some target modScales (filter cutoff = 4000 Hz, ringmod freq
      // = 1500 Hz) produce extreme audio modulation at the upper
      // end of the depth range, which combined with random sources
      // sometimes pushed the patch into runaway / silent territory.
      // Biased so small depths show up more often: abs(x)^1.3 pushes
      // probability mass toward the middle.
      const mag = Math.pow(rng(), 1.3) * 0.4;
      const depth = rng() < 0.5 ? -mag : mag;
      router.addConnection({
        sourceModuleId: source.id,
        targetModuleId: target.moduleId,
        targetParam: target.param,
        depth,
      });
    }
  }

  randomizeParam(param: AnyParameter, amount: number, rng: () => number): void {
    if (!param.randomizable) return;
    if (param.kind === 'numeric') {
      const currentNorm = param.toNormalized();
      const skew = param.randomSkew > 0 ? param.randomSkew : 1;
      // random^(1/skew): skew > 1 biases toward 1, skew < 1 biases toward 0.
      const raw = rng();
      const targetNorm = Math.pow(raw, 1 / skew);
      const nextNorm = currentNorm + (targetNorm - currentNorm) * amount;
      param.setNormalized(nextNorm, 'randomize');
      return;
    }
    // Enum: coin-flip weighted by amount.
    if (rng() >= amount) return;
    const options = param.options as readonly string[];
    if (options.length === 0) return;
    const idx = Math.min(options.length - 1, Math.floor(rng() * options.length));
    const choice = options[idx];
    if (choice !== undefined) param.set(choice, 'randomize');
  }
}
